import { gsap } from 'gsap';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Strict TypeScript interfaces
interface AnimationConfig {
  duration: number;
  offset: number;
  pillDuration: number;
}

interface WindowWithAnimations extends Window {
  animationsInitialized?: boolean;
  animationContext?: gsap.Context;
}

declare const window: WindowWithAnimations;

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

// Configuration constants for better maintainability
const ANIMATION_CONFIG: AnimationConfig = {
  duration: 0.5,
  offset: 0,
  pillDuration: 0,
};

const SELECTORS = {
  titleTarget: '[gsap="title_target"]',
  titleTrigger: '[gsap="title_trigger"]',
  titleTriggerAlt: '[gsap="title_trigger_alt"]',
  navSpacer: '[gsap="nav_spacer"]',
  navSpacerAlt: '[gsap="nav_spacer_alt"]',
  navMenu: '[gsap="nav_menu"]',
  navMenuAlt: '[gsap="nav_menu_alt"]',
  hero: '[gsap="hero"]',
  heroAlt: '[gsap="hero_alt"]',
  heroBackground: '[gsap="hero_background"]',
  cardsTrigger: '[gsap="cards_trigger"]',
  cardsTopRow: '[gsap="cards_top_row"]',
  tabImage: '[gsap="tab_image"]',
  pill: '[gsap="pill"]',
  pillTrigger: '[gsap="pill_trigger"]',
  ctaExpand: '[gsap="cta_expand"]',
  ctaImageList: '[gsap="cta_image_list"]',
  nav: '[gsap="nav"]',
  footer: '[gsap="footer"]',
  smoothContent: '#smooth-content',
} as const;

const mm = gsap.matchMedia();

// Utility functions with proper typing
const safeQuerySelector = (selector: string): HTMLElement | null => {
  return document.querySelector(selector) as HTMLElement | null;
};

const safeQuerySelectorAll = (selector: string): HTMLElement[] => {
  return Array.from(document.querySelectorAll(selector)) as HTMLElement[];
};

const createPillScrollTrigger = (
  item: HTMLElement,
  index: number,
  textBlocks: HTMLElement[],
  pillDuration: number,
  offset: number
): ScrollTrigger.Vars => ({
  trigger: item,
  start: `top-=${offset}px center`,
  end: 'bottom center',
  scrub: false,
  markers: false,
  onEnter: () => highlightPill(item, index, textBlocks, pillDuration),
  onLeave: () => resetPill(item, index, textBlocks, pillDuration),
  onEnterBack: () => highlightPill(item, index, textBlocks, pillDuration),
  onLeaveBack: () => resetPill(item, index, textBlocks, pillDuration),
});

const highlightPill = (
  item: HTMLElement,
  index: number,
  textBlocks: HTMLElement[],
  pillDuration: number
): void => {
  gsap.to(item, {
    backgroundColor: 'var(--base-color-neutral--950)',
    color: 'var(--base-color-neutral--50)',
    duration: pillDuration,
  });

  if (index !== 0) {
    gsap.to(textBlocks[0], {
      backgroundColor: '',
      color: '',
      duration: pillDuration,
    });
  }

  if (index !== textBlocks.length - 1) {
    gsap.to(textBlocks[textBlocks.length - 1], {
      backgroundColor: '',
      color: '',
      duration: pillDuration,
    });
  }
};

const resetPill = (
  item: HTMLElement,
  index: number,
  textBlocks: HTMLElement[],
  pillDuration: number
): void => {
  if (index !== textBlocks.length - 1 && index !== 0) {
    gsap.to(item, {
      backgroundColor: '',
      color: '',
      duration: pillDuration,
    });
  }
};

// Optimization: Use object for reusable animation properties
const COMMON_ANIMATIONS = {
  pinToBack: {
    z: -200,
    opacity: 0,
    filter: 'blur(5px)',
  },
  ctaImage: {
    z: -150,
    duration: ANIMATION_CONFIG.duration,
    filter: 'blur(16px)',
    ease: 'power2.out',
  },
} as const;

// Initialize default states - moved to function to ensure DOM is ready
const initializeDefaultStates = (): void => {
  const titleTargets = safeQuerySelectorAll(SELECTORS.titleTarget);
  if (titleTargets.length > 0) {
    gsap.set(titleTargets, {
      z: 0,
      opacity: 1,
      filter: 'none',
    });
  }

  const navSpacerAlt = safeQuerySelector(SELECTORS.navSpacerAlt);
  if (navSpacerAlt) {
    gsap.set(navSpacerAlt, { backgroundColor: '#7dcbd9' });
  }
};

// Animation functions with better error handling and performance optimization
const setupHeroTitleAnimation = (): void => {
  const titleTrigger = safeQuerySelector(SELECTORS.titleTrigger);
  if (!titleTrigger) return;

  mm.add('(min-width: 992px)', () => {
    const heroTitleTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: titleTrigger,
        start: 'clamp(center center)',
        scrub: true,
        pin: true,
        pinSpacing: false,
        anticipatePin: 1,
        markers: false,
        // Performance optimization: reduce refresh frequency
        refreshPriority: -1,
      },
    });

    const navSpacer = safeQuerySelector(SELECTORS.navSpacer);
    const titleTarget = safeQuerySelector(SELECTORS.titleTarget);
    const navMenu = safeQuerySelector(SELECTORS.navMenu);

    if (navSpacer) {
      heroTitleTimeline.to(navSpacer, { minHeight: '4rem', duration: 0.25 });
    }
    if (titleTarget) {
      heroTitleTimeline.to(titleTarget, COMMON_ANIMATIONS.pinToBack, '-=0.2');
    }
    if (navMenu) {
      heroTitleTimeline.from(navMenu, { backgroundColor: '#2A2C33', duration: 0.25 }, 0);
    }
  });
};

const setupHeroTitleAnimationAlt = (): void => {
  const titleTriggerAlt = safeQuerySelector(SELECTORS.titleTriggerAlt);
  if (!titleTriggerAlt) return;

  mm.add('(min-width: 992px)', () => {
    const heroTitleTimelineAlt = gsap.timeline({
      scrollTrigger: {
        trigger: titleTriggerAlt,
        start: 'clamp(center center)',
        end: 'start top',
        scrub: true,
        pin: false,
        pinSpacing: false,
        markers: false,
        refreshPriority: -1,
      },
    });

    const navSpacerAlt = safeQuerySelector(SELECTORS.navSpacerAlt);
    const navMenuAlt = safeQuerySelector(SELECTORS.navMenuAlt);

    if (navSpacerAlt) {
      heroTitleTimelineAlt.fromTo(navSpacerAlt, { minHeight: '8rem' }, { minHeight: '4rem' }, 0.25);
    }
    if (navMenuAlt) {
      heroTitleTimelineAlt.from(navMenuAlt, { backgroundColor: '#69b6c8', duration: 0.25 }, 0.25);
    }
  });
};

const setupHeroBackgroundAnimation = (): void => {
  const hero = safeQuerySelector(SELECTORS.hero);
  if (!hero) return;

  const heroBackgroundTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: hero,
      start: 'bottom center',
      end: '120% center',
      scrub: true,
      markers: false,
    },
  });

  const navSpacer = safeQuerySelector(SELECTORS.navSpacer);
  const heroBackground = safeQuerySelector(SELECTORS.heroBackground);

  if (heroBackground) {
    heroBackgroundTimeline.to(heroBackground, { backgroundColor: '#fff' });
  }
  if (navSpacer) {
    heroBackgroundTimeline
      .to(navSpacer, { backgroundColor: '#fff' }, 0)
      .to(navSpacer, { attr: { 'data-theme': 'light' } }, 0.175);
  }
};

const setupHeroBackgroundAnimationAlt = (): void => {
  const heroAlt = safeQuerySelector(SELECTORS.heroAlt);
  if (!heroAlt) return;

  const heroBackgroundTimelineAlt = gsap.timeline({
    scrollTrigger: {
      trigger: heroAlt,
      start: 'bottom center',
      end: '120% center',
      scrub: true,
      markers: false,
    },
  });

  const navSpacerAlt = safeQuerySelector(SELECTORS.navSpacerAlt);
  const heroBackground = safeQuerySelector(SELECTORS.heroBackground);

  if (heroBackground) {
    heroBackgroundTimelineAlt.to(heroBackground, { backgroundColor: '#fff' });
  }
  if (navSpacerAlt) {
    heroBackgroundTimelineAlt
      .fromTo(navSpacerAlt, { backgroundColor: '#7dcbd9' }, { backgroundColor: '#fff' }, 0)
      .to(navSpacerAlt, { attr: { 'data-theme': 'light' } }, 0);
  }
};

const setupCardsAnimation = (): void => {
  mm.add('(min-width: 767px)', () => {
    const triggers = safeQuerySelectorAll(SELECTORS.cardsTrigger);

    triggers.forEach((trigger) => {
      const topRow = trigger.querySelector(SELECTORS.cardsTopRow) as HTMLElement;

      if (topRow) {
        gsap.set(topRow, {
          z: 0,
          opacity: 1,
          filter: 'none',
        });

        const cardsTimeline = gsap.timeline({
          scrollTrigger: {
            trigger: trigger,
            start: 'center 40%',
            end: 'bottom top',
            scrub: true,
            pin: true,
            pinSpacing: false,
            anticipatePin: 1,
            markers: false,
          },
        });
        cardsTimeline.to(topRow, COMMON_ANIMATIONS.pinToBack);
      }
    });
  });
};

const setupTabImageAnimations = (): void => {
  const images = safeQuerySelectorAll(SELECTORS.tabImage);

  images.forEach((image) => {
    gsap.from(image, {
      scale: 1.1,
      duration: ANIMATION_CONFIG.duration,
      filter: 'blur(16px)',
      ease: 'power2.out',
      scrollTrigger: {
        trigger: image,
        start: 'top 85%',
        markers: false,
        // Performance: use once to avoid repeated triggers
        once: true,
      },
    });
  });
};

const setupPillAnimations = (): void => {
  const { pillDuration } = ANIMATION_CONFIG;
  const textBlocks = safeQuerySelectorAll(SELECTORS.pill);
  const pillTrigger = safeQuerySelector(SELECTORS.pillTrigger);

  if (textBlocks.length === 0 || !pillTrigger) return;

  mm.add('(min-width: 992px)', () => {
    const pillTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: pillTrigger,
        start: 'top center',
        end: 'bottom center',
        scrub: true,
        pin: true,
        anticipatePin: 1,
        markers: false,
        pinSpacing: false,
      },
    });

    const remValue = parseFloat((textBlocks[0].offsetHeight / 6.9).toFixed(4));
    const totalRemValue = (textBlocks.length - 1) * remValue;

    pillTimeline.to(textBlocks, {
      yPercent: -100 * (textBlocks.length - 1),
      y: -1 * totalRemValue,
      ease: 'none',
    });

    textBlocks.forEach((item, index) => {
      ScrollTrigger.create(
        createPillScrollTrigger(item, index, textBlocks, pillDuration, remValue)
      );
    });
  });

  mm.add('(max-width: 991px)', () => {
    const remValue = parseFloat((textBlocks[0].offsetHeight / 3.9 / 2).toFixed(4));
    textBlocks.forEach((item, index) => {
      ScrollTrigger.create(
        createPillScrollTrigger(item, index, textBlocks, pillDuration, remValue / 2)
      );
    });
  });

  // Initialize first pill state
  gsap.set(textBlocks[0], {
    backgroundColor: 'var(--base-color-neutral--950)',
    color: 'var(--base-color-neutral--50)',
    duration: pillDuration,
  });

  // Handle last pill animation
  ScrollTrigger.create({
    trigger: pillTrigger,
    start: 'bottom center',
    end: 'bottom bottom',
    scrub: false,
    markers: false,
    onEnter: () => {
      gsap.to(textBlocks[textBlocks.length - 1], {
        backgroundColor: 'var(--base-color-neutral--950)',
        color: 'var(--base-color-neutral--50)',
        duration: pillDuration,
      });
    },
    onLeaveBack: () => {
      gsap.to(textBlocks[textBlocks.length - 1], {
        backgroundColor: '',
        color: '',
        duration: pillDuration,
      });
    },
  });
};

const setupCtaAnimations = (): void => {
  const ctaExpand = safeQuerySelector(SELECTORS.ctaExpand);
  const ctaImageList = safeQuerySelector(SELECTORS.ctaImageList);

  if (ctaExpand) {
    const ctaTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: ctaExpand,
        start: 'top 90%',
        end: 'bottom center',
        scrub: true,
        markers: false,
      },
    });

    mm.add('(min-width: 479px)', () => {
      ctaTimeline.to(ctaExpand, {
        maxWidth: '100%',
      });
    });
  }

  if (ctaImageList) {
    const ctaImageTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: ctaImageList,
        start: 'top 95%',
        end: 'center center',
        scrub: true,
        markers: false,
      },
    });

    mm.add('(min-width: 479px)', () => {
      const imageSelectors = [
        '[gsap="cta_image_3"]',
        '[gsap="cta_image_2"]',
        '[gsap="cta_image_4"]',
        '[gsap="cta_image_1"]',
        '[gsap="cta_image_5"]',
      ];

      const timings = ['+=0.1', '-=0.25', '-=0.5', '-=0.25', '-=0.5'];

      imageSelectors.forEach((selector, index) => {
        const element = safeQuerySelector(selector);
        if (element) {
          ctaImageTimeline.from(element, COMMON_ANIMATIONS.ctaImage, timings[index]);
        }
      });
    });
  }
};

const setupNavAnimation = (): void => {
  const nav = safeQuerySelector(SELECTORS.nav);
  const footer = safeQuerySelector(SELECTORS.footer);

  if (nav && footer) {
    gsap.to(nav, {
      position: 'fixed',
      top: '-100%',
      scrollTrigger: {
        trigger: footer,
        scrub: true,
        start: 'top top',
      },
    });
  }
};

// Enhanced initialization with better error handling
const initializeAnimations = (): void => {
  try {
    // Initialize default states first
    initializeDefaultStates();

    const context = gsap.context(() => {
      // Batch ScrollTrigger creation for better performance
      ScrollTrigger.batch(SELECTORS.tabImage, {
        onEnter: (elements) => {
          gsap.from(elements, {
            scale: 1.1,
            duration: ANIMATION_CONFIG.duration,
            filter: 'blur(16px)',
            ease: 'power2.out',
            stagger: 0.1,
          });
        },
      });

      // Initialize all animations
      setupHeroTitleAnimation();
      setupHeroTitleAnimationAlt();
      setupHeroBackgroundAnimation();
      setupHeroBackgroundAnimationAlt();
      setupCardsAnimation();
      setupTabImageAnimations();
      setupPillAnimations();
      setupCtaAnimations();
      setupNavAnimation();

      // Refresh ScrollTrigger after all animations are set up
      ScrollTrigger.refresh();

      console.log('GSAP animations initialized successfully');

      // Initialize ScrollSmoother on desktop with performance optimization
      gsap.matchMedia().add('(min-width: 992px)', () => {
        const smoothContent = safeQuerySelector(SELECTORS.smoothContent);
        if (smoothContent) {
          ScrollSmoother.create({
            content: smoothContent,
            smooth: 0.66,
            effects: true,
            smoothTouch: 0.1,
            // Performance optimization
            normalizeScroll: true,
          });
        }
      });
    });

    window.animationContext = context;
    window.animationsInitialized = true;
  } catch (error) {
    console.error('Failed to initialize GSAP animations:', error);
  }
};

// Cleanup function with better error handling
const cleanupAnimations = (): void => {
  try {
    if (window.animationContext) {
      window.animationContext.revert();
      window.animationContext.kill();
      window.animationContext = undefined;
      console.log('GSAP animations cleaned up successfully');
    }
  } catch (error) {
    console.error('Error during animation cleanup:', error);
  }
};

window.Webflow = window.Webflow || [];
window.Webflow.push(() => {
  // Cleanup existing animations before reinitializing
  cleanupAnimations();

  // Initialize animations based on DOM ready state
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAnimations);
  } else {
    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(initializeAnimations);
  }

  // Enhanced MutationObserver with better error handling and performance
  const targetElement = safeQuerySelector('[data-wf-variation-617100005]');

  if (targetElement) {
    const observer = new MutationObserver((mutationsList) => {
      try {
        mutationsList.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'data-wf-hidden-variation'
          ) {
            if (!targetElement.hasAttribute('data-wf-hidden-variation')) {
              cleanupAnimations();
              // Use requestAnimationFrame for better performance
              requestAnimationFrame(initializeAnimations);
            }
          }
        });
      } catch (error) {
        console.error('Error in MutationObserver:', error);
      }
    });

    const config: MutationObserverInit = {
      attributes: true,
      attributeFilter: ['data-wf-hidden-variation'],
    };

    observer.observe(targetElement, config);
  } else {
    console.warn('Target element [data-wf-variation-617100005] not found');
  }
});

// Export for potential module usage
export { initializeAnimations, cleanupAnimations, ANIMATION_CONFIG, SELECTORS };
